<script setup lang="ts">
import { IconInfoCircle, IconPencil, IconX } from '@tabler/icons-vue';
import { computed, nextTick, ref } from 'vue';

import Tooltip from './Tooltip.vue';

interface Props {
  disabled?: boolean;
  duplicateMessage?: (_value: string) => string;
  error?: string;
  helpText?: string;
  id?: string;
  invalid?: boolean;
  itemLabel?: string;
  label?: string;
  modelValue?: null | string[];
  placeholder?: string;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  duplicateMessage: (value: string) => `Este valor '${value}' já foi adicionado`,
  error: '',
  helpText: '',
  id: 'tag-input',
  invalid: false,
  itemLabel: 'item',
  label: '',
  modelValue: null,
  placeholder: 'Digite e pressione Enter',
});

const emit = defineEmits<{
  'update:modelValue': [value: null | string[]];
}>();

const inputValue = ref('');
const editingIndex = ref<null | number>(null);
const editingValue = ref('');
const duplicateError = ref('');
const editInputRef = ref<HTMLInputElement | null>(null);

const tags = computed(() => props.modelValue ?? []);

const fieldError = computed(() => props.error || duplicateError.value);

const isFieldInvalid = computed(() => props.invalid || !!duplicateError.value);

const editInputStyle = computed(() => {
  const charCount = Math.max(editingValue.value.length, 1);
  return { width: `${charCount + 0.5}ch` };
});

const editAriaLabel = computed(() => `Editar ${props.itemLabel}`);

const removeAriaLabel = computed(() => `Remover ${props.itemLabel}`);

const updateTags = (newTags: string[]) => {
  emit('update:modelValue', newTags.length ? newTags : null);
};

const isDuplicateTag = (value: string, excludeIndex?: number) => (
  tags.value.some((tag, index) => index !== excludeIndex && tag === value)
);

const showDuplicateError = (value: string) => {
  duplicateError.value = props.duplicateMessage(value);
};

const clearDuplicateError = () => {
  duplicateError.value = '';
};

const addTag = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed || props.disabled) return;

  if (isDuplicateTag(trimmed)) {
    showDuplicateError(trimmed);
    return;
  }

  clearDuplicateError();
  updateTags([...tags.value, trimmed]);
  inputValue.value = '';
};

const removeTag = (index: number) => {
  if (props.disabled) return;
  clearDuplicateError();
  updateTags(tags.value.filter((_, i) => i !== index));
};

const onNewInput = () => {
  clearDuplicateError();
};

const startEdit = (index: number) => {
  if (props.disabled) return;
  editingIndex.value = index;
  editingValue.value = tags.value[index];
  nextTick(() => editInputRef.value?.focus());
};

const commitEdit = () => {
  if (editingIndex.value === null) return;

  const trimmed = editingValue.value.trim();
  const index = editingIndex.value;

  if (!trimmed) {
    editingIndex.value = null;
    removeTag(index);
    return;
  }

  if (isDuplicateTag(trimmed, index)) {
    showDuplicateError(trimmed);
    return;
  }

  editingIndex.value = null;
  clearDuplicateError();
  const newTags = [...tags.value];
  newTags[index] = trimmed;
  updateTags(newTags);
};

const cancelEdit = () => {
  editingIndex.value = null;
};

const onInputKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    addTag(inputValue.value);
  }
};

const onEditKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    event.preventDefault();
    commitEdit();
  }
  if (event.key === 'Escape') {
    cancelEdit();
  }
};
</script>

<template>
  <div class="tag-input-field">
    <div v-if="label" class="tag-input-label">
      <label class="form-label mb-0" :for="id">{{ label }}</label>
      <Tooltip v-if="helpText" :content="helpText" placement="top">
        <IconInfoCircle
          :size="16"
          :stroke-width="2"
          class="tag-input-info-icon"
          tabindex="0"
        />
      </Tooltip>
    </div>

    <div
      class="tag-input form-control"
      :class="{ 'is-invalid': isFieldInvalid, 'tag-input--disabled': disabled }"
    >
      <span
        v-for="(tag, index) in tags"
        :key="`${tag}-${index}`"
        class="tag-input-tag"
        :class="{ 'tag-input-tag--editing': editingIndex === index }"
      >
        <input
          v-if="editingIndex === index"
          ref="editInputRef"
          v-model="editingValue"
          type="text"
          class="tag-input-edit"
          :style="editInputStyle"
          :aria-label="editAriaLabel"
          :disabled="disabled"
          @keydown="onEditKeydown"
          @blur="commitEdit"
        />
        <template v-else>
          <span class="tag-input-tag-text">{{ tag }}</span>
          <button
            v-if="!disabled"
            type="button"
            class="tag-input-tag-action"
            :aria-label="editAriaLabel"
            @click="startEdit(index)"
          >
            <IconPencil :size="14" :stroke-width="2" />
          </button>
          <button
            v-if="!disabled"
            type="button"
            class="tag-input-tag-action"
            :aria-label="removeAriaLabel"
            @click="removeTag(index)"
          >
            <IconX :size="14" :stroke-width="2" />
          </button>
        </template>
      </span>

      <input
        :id="id"
        v-model="inputValue"
        type="text"
        class="tag-input-new"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="onNewInput"
        @keydown="onInputKeydown"
      />
    </div>

    <div v-if="fieldError" class="invalid-feedback d-block">
      {{ fieldError }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.tag-input-field {
  width: 100%;
}

.tag-input-label {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
}

.tag-input-info-icon {
  color: #6c757d;
  cursor: help;
  flex-shrink: 0;
}

.tag-input {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.375rem;
  min-height: calc(1.5em + 0.75rem + 2px);
  height: auto;
  padding: 0.375rem 0.5rem;

  &--disabled {
    background-color: #e9ecef;
    cursor: not-allowed;
  }
}

.tag-input-tag {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: #e9ecef;
  border-radius: 0.25rem;
  padding: 0.125rem 0.25rem 0.125rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.25;
  max-width: fit-content;

  &--editing {
    padding-right: 0.375rem;
  }
}

.tag-input-tag-text {
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tag-input-tag-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: #212529;
  }
}

.tag-input-edit {
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.875rem;
  line-height: 1.25;
  min-width: 0;
  max-width: 12rem;
  padding: 0;
  field-sizing: content;
}

.tag-input-new {
  flex: 1;
  min-width: 8rem;
  border: none;
  outline: none;
  background: transparent;
  font-size: 1rem;
  padding: 0.125rem 0.25rem;

  &:disabled {
    cursor: not-allowed;
  }

  &::placeholder {
    color: #6c757d;
  }
}

.invalid-feedback {
  display: block;
  font-size: 0.875rem;
  color: #dc3545;
  margin-top: 0.25rem;
}
</style>
